/**
 * IES lamp GPU packaging.
 *
 * Responsibilities:
 *   1. Resample each unique IES distribution onto a canonical (AZ x EL) grid so
 *      all layers of the candela GPUTexture2DArray share the same dimensions.
 *   2. Produce a Float32Array "layers buffer" packed as layers * AZ * EL floats
 *      ready to upload as a 2D-array texture (r32float).
 *   3. Produce a per-lamp struct array (std430-compatible) with world position,
 *      orientation, ies layer index, and intensity scale.
 *
 * The azimuth axis is mapped to the U coordinate (0..1 -> 0..2*PI), elevation
 * to V (0..1 -> 0..PI). The WGSL sampler uses bilinear filtering in UV.
 */

export interface IesGrid {
  /** Azimuth angles in radians, sorted ascending, range [0, 2*PI). */
  azimuth: number[];
  /** Elevation angles in radians, sorted ascending, range [0, PI]. */
  elevation: number[];
  /** candela[iAzimuth][iElevation] in candela (cd). */
  candela: number[][];
}

export interface LampPathtracerInput {
  /** World-space position (meters, Z-up). */
  position: [number, number, number];
  /** Rotation of the IES local frame around the world Z axis (radians). */
  rotationZ: number;
  /** Which unique IES grid this lamp uses. */
  iesKey: string;
  /** Scalar multiplier on candela output (usually 1.0). */
  intensityScale: number;
  /**
   * Effective source radius in meters. NEE jitters on a sphere of this radius
   * around the lamp, which softens the IES cone edge and shadow boundaries.
   * Set to 0 for an idealized point source (sharp edges).
   */
  radius: number;
}

export interface PackedIesData {
  /** Canonical azimuth grid width (U dimension). */
  azSize: number;
  /** Canonical elevation grid height (V dimension). */
  elSize: number;
  /** Float32Array of length layers * azSize * elSize (row-major, V x U per layer). */
  candelaLayers: Float32Array;
  /** Number of IES layers. */
  layerCount: number;
  /** Max candela value across all layers; informational (engine may use for scaling). */
  maxCandela: number;
}

interface PackedLamp {
  position: [number, number, number];
  rotationZ: number;
  iesLayer: number;
  intensityScale: number;
  radius: number;
}

/**
 * Resample one IES grid onto (azSize x elSize) via bilinear interpolation.
 * The canonical grid is uniform: az = i / azSize * 2*PI, el = j / (elSize-1) * PI.
 */
function resampleGrid(grid: IesGrid, azSize: number, elSize: number): Float32Array {
  const out = new Float32Array(azSize * elSize);
  const az = grid.azimuth;
  const el = grid.elevation;
  const nAz = az.length;
  const nEl = el.length;
  if (nAz === 0 || nEl === 0) return out;

  // Bilinear: binary search each axis
  function lerpAxis(sample: number, values: number[]): { lo: number; hi: number; t: number } {
    const n = values.length;
    if (n === 1) return { lo: 0, hi: 0, t: 0 };
    if (sample <= values[0]) return { lo: 0, hi: 0, t: 0 };
    if (sample >= values[n - 1]) return { lo: n - 1, hi: n - 1, t: 0 };
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const m = (lo + hi) >> 1;
      if (values[m] <= sample) lo = m;
      else hi = m;
    }
    const t = (sample - values[lo]) / Math.max(1e-9, values[hi] - values[lo]);
    return { lo, hi, t };
  }

  for (let v = 0; v < elSize; v++) {
    const elSample = elSize > 1 ? (v / (elSize - 1)) * Math.PI : 0;
    const ey = lerpAxis(elSample, el);
    for (let u = 0; u < azSize; u++) {
      const azSample = (u / azSize) * 2 * Math.PI;
      const ax = lerpAxis(azSample, az);
      const c00 = grid.candela[ax.lo]?.[ey.lo] ?? 0;
      const c10 = grid.candela[ax.hi]?.[ey.lo] ?? 0;
      const c01 = grid.candela[ax.lo]?.[ey.hi] ?? 0;
      const c11 = grid.candela[ax.hi]?.[ey.hi] ?? 0;
      const cx0 = c00 * (1 - ax.t) + c10 * ax.t;
      const cx1 = c01 * (1 - ax.t) + c11 * ax.t;
      out[v * azSize + u] = cx0 * (1 - ey.t) + cx1 * ey.t;
    }
  }
  return out;
}

/**
 * Separable Gaussian blur on a single (azSize * elSize) candela layer, with
 * proper angular topology: the azimuth axis wraps at the 2π seam, and the
 * elevation axis is clamped at nadir/zenith. `sigmaAz` and `sigmaEl` are
 * measured in texels of the canonical grid (one texel = 360°/azSize in
 * azimuth and 180°/(elSize-1) in elevation).
 */
function gaussianBlurLayer(
  src: Float32Array,
  azSize: number,
  elSize: number,
  sigmaAz: number,
  sigmaEl: number,
): Float32Array {
  if (sigmaAz <= 0 && sigmaEl <= 0) return src;

  function buildKernel(sigma: number): number[] {
    if (sigma <= 0) return [1];
    const radius = Math.max(1, Math.ceil(sigma * 3));
    const k: number[] = [];
    let sum = 0;
    for (let i = -radius; i <= radius; i++) {
      const w = Math.exp(-(i * i) / (2 * sigma * sigma));
      k.push(w);
      sum += w;
    }
    for (let i = 0; i < k.length; i++) k[i] /= sum;
    return k;
  }

  const kAz = buildKernel(sigmaAz);
  const kEl = buildKernel(sigmaEl);
  const rAz = (kAz.length - 1) >> 1;
  const rEl = (kEl.length - 1) >> 1;

  // Horizontal (azimuth) pass, with wrap-around.
  const tmp = new Float32Array(azSize * elSize);
  for (let v = 0; v < elSize; v++) {
    const rowBase = v * azSize;
    for (let u = 0; u < azSize; u++) {
      let acc = 0;
      for (let k = -rAz; k <= rAz; k++) {
        const uu = (((u + k) % azSize) + azSize) % azSize;
        acc += src[rowBase + uu] * kAz[k + rAz];
      }
      tmp[rowBase + u] = acc;
    }
  }

  // Vertical (elevation) pass, clamped at the poles.
  const out = new Float32Array(azSize * elSize);
  for (let v = 0; v < elSize; v++) {
    for (let u = 0; u < azSize; u++) {
      let acc = 0;
      for (let k = -rEl; k <= rEl; k++) {
        let vv = v + k;
        if (vv < 0) vv = 0;
        else if (vv >= elSize) vv = elSize - 1;
        acc += tmp[vv * azSize + u] * kEl[k + rEl];
      }
      out[v * azSize + u] = acc;
    }
  }
  return out;
}

/**
 * Combine a list of lamps and their corresponding IES grids into GPU-friendly data.
 *
 * @param lamps           One entry per lamp, referencing an iesKey.
 * @param iesGridsByKey   Unique IES grids keyed by iesKey.
 * @param azSize          Canonical azimuth resolution (recommend 128).
 * @param elSize          Canonical elevation resolution (recommend 64).
 * @param blurSigmaDeg    Angular Gaussian blur sigma in degrees. Applied to
 *                        the resampled IES grid before GPU upload; this is
 *                        the primary knob for softening the otherwise razor-
 *                        sharp cone boundary that many IES distributions
 *                        contain as a hard cutoff. 0 = no blur.
 */
export function packIesData(
  lamps: LampPathtracerInput[],
  iesGridsByKey: Map<string, IesGrid>,
  azSize = 128,
  elSize = 64,
  blurSigmaDeg = 0,
): { ies: PackedIesData; lamps: PackedLamp[] } {
  const keyOrder: string[] = [];
  const keyToLayer = new Map<string, number>();
  for (const lamp of lamps) {
    if (!iesGridsByKey.has(lamp.iesKey)) continue;
    if (!keyToLayer.has(lamp.iesKey)) {
      keyToLayer.set(lamp.iesKey, keyOrder.length);
      keyOrder.push(lamp.iesKey);
    }
  }

  const layerCount = Math.max(1, keyOrder.length);
  const stride = azSize * elSize;
  const candelaLayers = new Float32Array(layerCount * stride);

  // Convert degree-space sigma to grid-texel space. One azimuth texel spans
  // 360/azSize degrees and one elevation texel spans 180/(elSize-1) degrees.
  const sigmaAzTex = blurSigmaDeg > 0 ? blurSigmaDeg / (360 / azSize) : 0;
  const sigmaElTex = blurSigmaDeg > 0 ? blurSigmaDeg / (180 / Math.max(1, elSize - 1)) : 0;

  let maxCandela = 0;
  for (let l = 0; l < keyOrder.length; l++) {
    const grid = iesGridsByKey.get(keyOrder[l])!;
    const resampled = resampleGrid(grid, azSize, elSize);
    const blurred = blurSigmaDeg > 0 ? gaussianBlurLayer(resampled, azSize, elSize, sigmaAzTex, sigmaElTex) : resampled;
    candelaLayers.set(blurred, l * stride);
    for (let i = 0; i < blurred.length; i++) {
      if (blurred[i] > maxCandela) maxCandela = blurred[i];
    }
  }

  const packedLamps: PackedLamp[] = [];
  for (const lamp of lamps) {
    const layer = keyToLayer.get(lamp.iesKey);
    if (layer === undefined) continue;
    packedLamps.push({
      position: lamp.position,
      rotationZ: lamp.rotationZ,
      iesLayer: layer,
      intensityScale: lamp.intensityScale,
      radius: lamp.radius,
    });
  }

  return {
    ies: {
      azSize,
      elSize,
      candelaLayers,
      layerCount,
      maxCandela,
    },
    lamps: packedLamps,
  };
}

/**
 * Pack lamp structs into a std430-compatible Float32Array suitable for upload as
 * a storage buffer. Layout per lamp (32 bytes / 8 floats):
 *   [0..2]  position (vec3<f32>)
 *   [3]     rotationZ (f32)
 *   [4]     iesLayer (u32 reinterpreted as f32)
 *   [5]     intensityScale (f32)
 *   [6]     radius (f32)
 *   [7]     padding
 */
export function packLampBuffer(lamps: PackedLamp[]): Float32Array {
  const STRIDE = 8;
  const out = new Float32Array(Math.max(1, lamps.length) * STRIDE);
  const uintView = new Uint32Array(out.buffer);
  for (let i = 0; i < lamps.length; i++) {
    const base = i * STRIDE;
    out[base + 0] = lamps[i].position[0];
    out[base + 1] = lamps[i].position[1];
    out[base + 2] = lamps[i].position[2];
    out[base + 3] = lamps[i].rotationZ;
    uintView[base + 4] = lamps[i].iesLayer >>> 0;
    out[base + 5] = lamps[i].intensityScale;
    out[base + 6] = lamps[i].radius;
    out[base + 7] = 0;
  }
  return out;
}
